import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSessionToken, getUserDetails } from '../../Services/services';
import { ROLES, hasRole, hasConnectRole } from '../../utils/roles';

const SEARCH_DEBOUNCE_MS = 400;
export const PAGE_SIZE = 10;

/**
 * Shared state/behaviour for the ACON-60 marketplace list views (Invoices,
 * RMAs, Shipments). Handles guest/pending/connect-role gating, server-side
 * pagination (10/page), debounced search, and sort — so each view only has to
 * render its own columns.
 *
 * @param {object}   opts
 * @param {(params:object)=>Promise<any>} opts.fetcher       API function, e.g. getInvoices.
 * @param {string}   opts.requiredConnectRole                Connect role gating the view (e.g. 'invoices').
 * @param {object}   [opts.extraParams]                      ACON-65: extra server-side filter params
 *   (e.g. { type, dateFrom, dateTo, minTotal, maxTotal }) merged into every fetch call. Pass a plain
 *   object built fresh each render — it's compared by value (JSON.stringify), not identity.
 */
export function useMarketplaceList({ fetcher, requiredConnectRole, extraParams = {} }) {
  const auth = useSelector((state) => state.auth);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [userRole, setUserRole] = useState(auth.role || ROLES.PENDING);
  const [connectRoles, setConnectRoles] = useState(auth.connectRoles || []);
  const [roleReady, setRoleReady] = useState(!!auth.role);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(1);

  const isGuest = !getSessionToken();
  const isPending = roleReady && !isGuest && !hasRole(userRole, ROLES.STANDARD);
  // Only meaningful once roles are known; treated as "no access" until then so
  // we never flash restricted data before the role check resolves.
  const hasAccess = hasConnectRole(connectRoles, requiredConnectRole);

  // Resolve fresh role + connect roles from Salesforce on mount so a direct
  // navigation (or refresh) to this route gates correctly without relying on
  // Redux having been hydrated already.
  useEffect(() => {
    if (isGuest) { setRoleReady(true); return; }
    getUserDetails()
      .then((resp) => {
        const data = resp?.data || {};
        setUserRole(data.role || auth.role || ROLES.PENDING);
        if (Array.isArray(data.roles)) setConnectRoles(data.roles);
      })
      .catch(() => setUserRole(auth.role || ROLES.PENDING))
      .finally(() => setRoleReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // extraParams is a new object every render, so key it by value for the effect/callback
  // dependency array rather than relying on (unstable) reference identity.
  const extraParamsKey = JSON.stringify(extraParams || {});

  const fetchList = useCallback(async () => {
    if (isGuest || !roleReady) return;
    if (isPending || !hasAccess) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await fetcher({
        page,
        limit: PAGE_SIZE,
        sort: sortBy,
        keyword: searchTerm || undefined,
        ...extraParams,
      });
      const list = Array.isArray(data) ? data : (data?.records || []);
      const count = Array.isArray(data) ? list.length : (data?.total_count ?? list.length);
      setRows(list);
      setTotalCount(count);
    } catch (e) {
      setError(e);
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, roleReady, isPending, hasAccess, page, sortBy, searchTerm, fetcher, extraParamsKey]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Reset to page 1 whenever a filter changes (mirrors the search-debounce behaviour above).
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraParamsKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return {
    rows, totalCount, totalPages, loading, error,
    isGuest, isPending, hasAccess, roleReady,
    searchInput, setSearchInput, sortBy, setSortBy, page, setPage,
    refetch: fetchList,
  };
}
